/**
 * SettingsScene — Future Cartridge audio + camera + controls modal
 * (PR 4 of TODOs #24).
 *
 * DOM-overlay glass card with three sections:
 *
 *   - AUDIO     music + SFX sliders + mute toggle
 *   - CAMERA    zoom multiplier slider
 *   - CONTROLS  Direct / Command / Classic radios (+ inline description)
 *   - footer    Auto-saved indicator + build SHA + Reset Run CTA
 *
 * Sliders use native <input type='range'> with custom CSS — browser
 * drag-to-scrub for free + accessibility-correct keyboard interaction.
 * Radios are button-based for richer styling (selected-state dot +
 * inline description).
 *
 * Lifecycle: caller.scene.sleep() + scene.launch('SettingsScene') opens
 * us; close() stops + wakes WorldScene. Tap the backdrop, ESC, or B
 * also closes.
 */
import Phaser from 'phaser';
import { runState } from '../state/runState';
import { saveManager } from '../state/saveManager';
import { audio } from '../systems/audio';
import {
  getControlMode,
  setControlMode,
  type ControlMode,
} from '../state/controlMode';
import {
  ZOOM_RANGE,
  getZoomMultiplier,
  setZoomMultiplier,
} from '../state/cameraPrefs';
import type { WorldScene } from './WorldScene';
import type { SnapshotHandle } from '../render/snapshotBackdrop';
import {
  getReduceAnimations,
  setReduceAnimations,
} from '../state/motionPrefs';
import { syncMotionBodyClass } from '../render/motion';

/** Glass-Over-World PR 3b: live (transparent overlay) vs frozen
 *  (snapshot + WorldScene asleep). Determined at init from the
 *  presence of snapshotHandle in the data payload. Live launches via
 *  pokeballMenu.setModalOverlayOpen + scene.launch; frozen launches via
 *  WorldScene.launchFrozenModal. Drives the close() branch. */
type OverlayMode = 'live' | 'frozen';

export interface SettingsSceneData {
  snapshotHandle?: SnapshotHandle | null;
}

declare const __BUILD_SHA__: string;
declare const __BUILD_DATE__: string;

const CONTROL_MODE_RADIOS: ReadonlyArray<{
  value: ControlMode;
  label: string;
  desc: string;
}> = [
  {
    value: 'direct',
    label: 'Direct',
    desc: 'Touch anywhere below to walk. Lift to stop. Tap people and signs to talk.',
  },
  {
    value: 'command',
    label: 'Command',
    desc: 'Tap a tile to walk there. Tap a person to walk over and chat.',
  },
  {
    value: 'classic',
    label: 'Classic D-pad',
    desc: 'The original buttons. Drag the D-pad to walk; A talks, B closes.',
  },
];

function formatZoomPercent(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function formatVolumePercent(v: number): string {
  return `${Math.round(v * 100)}%`;
}

export class SettingsScene extends Phaser.Scene {
  private modalRoot: HTMLDivElement | null = null;
  /** Refs we need to update on toggles/slider changes without re-mounting
   *  the entire DOM tree. */
  private muteBtn: HTMLButtonElement | null = null;
  private musicValue: HTMLSpanElement | null = null;
  private sfxValue: HTMLSpanElement | null = null;
  private zoomValue: HTMLSpanElement | null = null;
  private controlRadios: HTMLButtonElement[] = [];
  private overlayMode: OverlayMode = 'live';
  private snapshotHandle: SnapshotHandle | null = null;

  constructor() {
    super('SettingsScene');
  }

  init(data: SettingsSceneData): void {
    this.snapshotHandle = data.snapshotHandle ?? null;
    this.overlayMode = this.snapshotHandle ? 'frozen' : 'live';
  }

  create(): void {
    // Glass-Over-World PR 3b: frozen path claims WorldScene's snapshot
    // FIRST; live path leaves the camera transparent so WorldScene
    // keeps rendering behind the DOM modal.
    if (this.snapshotHandle) {
      this.snapshotHandle.transferOwnership(this);
    } else {
      this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');
    }
    // Phaser swallow rect: any tap that misses the DOM modal falls
    // onto this no-op rect so the joystick / dialogue under WorldScene
    // doesn't react. PartySheetScene precedent. Added AFTER any
    // snapshot Image so it picks pointer events on top.
    this.add
      .rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0)
      .setOrigin(0)
      .setInteractive()
      .on('pointerdown', () => undefined);
    this.mountDOM();

    this.input.keyboard?.on('keydown-ESC', () => this.close());
    this.input.keyboard?.on('keydown-B', () => this.close());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private mountDOM(): void {
    const gameEl = document.getElementById('game');
    if (!gameEl) return;

    const root = document.createElement('div');
    root.className = 'settings-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Settings');
    root.addEventListener('click', (e) => {
      if (e.target === root) this.close();
    });

    const card = document.createElement('div');
    card.className = 'settings-card';

    // Header
    const header = document.createElement('header');
    header.className = 'settings-card__header';
    const title = document.createElement('h1');
    title.className = 'settings-card__title';
    title.textContent = 'Settings';
    header.appendChild(title);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'settings-card__close';
    close.setAttribute('aria-label', 'Close settings');
    close.textContent = '×';
    close.addEventListener('click', () => this.close());
    header.appendChild(close);
    card.appendChild(header);

    // Scrollable body
    const body = document.createElement('div');
    body.className = 'settings-card__body';

    body.appendChild(this.buildAudioSection());
    body.appendChild(this.buildCameraSection());
    body.appendChild(this.buildVisualsSection());
    body.appendChild(this.buildControlsSection());

    card.appendChild(body);
    card.appendChild(this.buildFooter());

    root.appendChild(card);
    gameEl.appendChild(root);
    this.modalRoot = root;
  }

  private buildAudioSection(): DocumentFragment {
    const frag = document.createDocumentFragment();

    const label = document.createElement('h2');
    label.className = 'settings-card__section-label';
    label.textContent = 'Audio';
    frag.appendChild(label);

    frag.appendChild(
      this.buildSliderRow({
        label: 'Music',
        min: 0,
        max: 1,
        step: 0.01,
        value: audio.musicVolume,
        format: formatVolumePercent,
        onInput: (v) => audio.setMusicVolume(v),
        valueRefSetter: (el) => (this.musicValue = el),
      }),
    );

    frag.appendChild(
      this.buildSliderRow({
        label: 'SFX',
        min: 0,
        max: 1,
        step: 0.01,
        value: audio.sfxVolume,
        format: formatVolumePercent,
        onInput: (v) => audio.setSfxVolume(v),
        valueRefSetter: (el) => (this.sfxValue = el),
      }),
    );

    const mute = document.createElement('button');
    mute.type = 'button';
    mute.className = 'settings-mute';
    mute.setAttribute('data-muted', audio.muted ? 'true' : 'false');
    mute.setAttribute('aria-pressed', audio.muted ? 'true' : 'false');
    mute.textContent = audio.muted ? 'Muted' : 'Mute';
    mute.addEventListener('click', () => {
      audio.setMuted(!audio.muted);
      this.refreshMute();
    });
    this.muteBtn = mute;
    frag.appendChild(mute);

    return frag;
  }

  private buildCameraSection(): DocumentFragment {
    const frag = document.createDocumentFragment();

    const label = document.createElement('h2');
    label.className = 'settings-card__section-label';
    label.textContent = 'Camera';
    frag.appendChild(label);

    frag.appendChild(
      this.buildSliderRow({
        label: 'Zoom',
        min: ZOOM_RANGE.min,
        max: ZOOM_RANGE.max,
        step: 0.05,
        value: getZoomMultiplier(),
        format: formatZoomPercent,
        onInput: (v) => {
          setZoomMultiplier(v);
          const ws = this.scene.get('WorldScene') as WorldScene | undefined;
          ws?.applyCameraZoomPref();
        },
        valueRefSetter: (el) => (this.zoomValue = el),
      }),
    );

    return frag;
  }

  /** Visuals — the "Reduce animations" toggle, the game's ONLY motion
   *  reduction path (policy 2026-08-02: the OS prefers-reduced-motion
   *  setting is deliberately ignored so combat FX reach everyone; this
   *  toggle is the accessibility valve). Mirrors the mute-button idiom. */
  private buildVisualsSection(): DocumentFragment {
    const frag = document.createDocumentFragment();

    const label = document.createElement('h2');
    label.className = 'settings-card__section-label';
    label.textContent = 'Visuals';
    frag.appendChild(label);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'settings-mute';
    const paint = (): void => {
      const on = getReduceAnimations();
      toggle.setAttribute('data-muted', on ? 'true' : 'false');
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      toggle.textContent = on ? 'Animations reduced' : 'Reduce animations';
    };
    paint();
    toggle.addEventListener('click', () => {
      setReduceAnimations(!getReduceAnimations());
      syncMotionBodyClass();
      paint();
      audio.playSfx('select');
    });
    frag.appendChild(toggle);

    return frag;
  }

  private buildControlsSection(): DocumentFragment {
    const frag = document.createDocumentFragment();

    const label = document.createElement('h2');
    label.className = 'settings-card__section-label';
    label.textContent = 'Controls';
    frag.appendChild(label);

    this.controlRadios = [];
    const current = getControlMode();
    for (const opt of CONTROL_MODE_RADIOS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'settings-radio';
      const isSelected = opt.value === current;
      btn.setAttribute('data-selected', isSelected ? 'true' : 'false');
      btn.setAttribute('data-mode', opt.value);
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      const row = document.createElement('span');
      row.className = 'settings-radio__row';
      const bullet = document.createElement('span');
      bullet.className = 'settings-radio__bullet';
      const labelText = document.createElement('span');
      labelText.className = 'settings-radio__label';
      labelText.textContent = opt.label;
      row.appendChild(bullet);
      row.appendChild(labelText);
      btn.appendChild(row);

      const desc = document.createElement('span');
      desc.className = 'settings-radio__desc';
      desc.textContent = opt.desc;
      btn.appendChild(desc);

      btn.addEventListener('click', () => {
        audio.playSfx('select');
        setControlMode(opt.value);
        const ws = this.scene.get('WorldScene') as WorldScene | undefined;
        ws?.setControlMode(opt.value);
        this.refreshControlMode(opt.value);
      });

      this.controlRadios.push(btn);
      frag.appendChild(btn);
    }

    return frag;
  }

  private buildFooter(): HTMLElement {
    const footer = document.createElement('footer');
    footer.className = 'settings-card__footer';

    const info = document.createElement('div');
    info.className = 'settings-card__footer-info';

    const autosave = document.createElement('span');
    autosave.className = 'settings-card__autosave';
    autosave.textContent = '✓ Auto-saved';
    info.appendChild(autosave);

    const build = document.createElement('span');
    build.className = 'settings-card__build';
    build.textContent = `build ${__BUILD_SHA__} · ${__BUILD_DATE__.slice(0, 10)}`;
    info.appendChild(build);

    footer.appendChild(info);

    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'settings-card__reset';
    reset.textContent = 'Reset Run';
    reset.setAttribute(
      'aria-label',
      'Reset Run — wipe save and reload',
    );
    reset.addEventListener('click', () => this.resetRun());
    footer.appendChild(reset);

    return footer;
  }

  private buildSliderRow(opts: {
    label: string;
    min: number;
    max: number;
    step: number;
    value: number;
    format: (v: number) => string;
    onInput: (v: number) => void;
    valueRefSetter: (el: HTMLSpanElement) => void;
  }): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'settings-row';

    const labelEl = document.createElement('span');
    labelEl.className = 'settings-row__label';
    labelEl.textContent = opts.label;
    row.appendChild(labelEl);

    const input = document.createElement('input');
    input.type = 'range';
    input.className = 'settings-slider';
    input.min = String(opts.min);
    input.max = String(opts.max);
    input.step = String(opts.step);
    input.value = String(opts.value);
    input.setAttribute('aria-label', opts.label);

    const valueEl = document.createElement('span');
    valueEl.className = 'settings-row__value';
    valueEl.textContent = opts.format(opts.value);

    input.addEventListener('input', () => {
      const v = parseFloat(input.value);
      if (Number.isNaN(v)) return;
      opts.onInput(v);
      valueEl.textContent = opts.format(v);
    });

    row.appendChild(input);
    row.appendChild(valueEl);
    opts.valueRefSetter(valueEl);

    return row;
  }

  private refreshMute(): void {
    if (!this.muteBtn) return;
    const muted = audio.muted;
    this.muteBtn.setAttribute('data-muted', muted ? 'true' : 'false');
    this.muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
    this.muteBtn.textContent = muted ? 'Muted' : 'Mute';
  }

  private refreshControlMode(active: ControlMode): void {
    for (const btn of this.controlRadios) {
      const mode = btn.getAttribute('data-mode');
      const isSelected = mode === active;
      btn.setAttribute('data-selected', isSelected ? 'true' : 'false');
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    }
  }

  private resetRun(): void {
    const ok = window.confirm(
      'Reset run? This wipes your party and progress and reloads the page.',
    );
    if (!ok) return;
    runState.reset();
    saveManager.clear();
    window.location.reload();
  }

  private close(): void {
    audio.playSfx('select');
    if (this.overlayMode === 'live') {
      // Live path: WorldScene never slept. Flip the overlay flag FIRST
      // so orbs un-dim + joystick re-enables immediately, THEN stop.
      // No wake — WorldScene was never asleep.
      const world = this.scene.get('WorldScene') as Phaser.Scene & {
        setModalOverlayOpen?: (open: boolean, source: 'settings') => void;
      };
      world.setModalOverlayOpen?.(false, 'settings');
      this.scene.stop();
    } else {
      // Frozen path: WorldScene was slept by launchFrozenModal.
      this.scene.stop();
      this.scene.wake('WorldScene');
    }
  }

  private teardown(): void {
    this.modalRoot?.remove();
    this.modalRoot = null;
    this.muteBtn = null;
    this.musicValue = null;
    this.sfxValue = null;
    this.zoomValue = null;
    this.controlRadios = [];
  }
}
